import { createAsyncThunk, createSlice, PayloadAction } from "@reduxjs/toolkit";
import { API_URLS } from "./apis/urls";
import Api from "./utils/apiCall";

// Define the types for SAP Worklist items
export interface WorklistItem {
  id: number;
  workItemId: string;
  workflowID: string;
  requestID?: string;
  workItemText: string;
  taskText: string;
  approverId: string;
  creator: string;
  type?: string;
  status: string;
  empId?: string;
  creationDate?: string;
  creationTime?: string;
  created_at: string;
  modified_at: string;
  sapCreatedAt?: string;
  sapModifiedAt?: string;
  deleted_at?: string;
}

// NOC Data interface
export interface NOCData {
  created_at: string;
  modified_at: string;
  deleted_at?: string;
  id: number;
  is_draft: boolean;
  status: string;
  referenceNumber: string;
  empID: string;
  whetherProbation?: string;
  whetherProbationConfirmed?: string;
  trainedAbroadByDM?: string;
  appNoInYear?: string;
  placeofPosting?: string;
  natureOfOutside?: string;
  postAppliedFor?: string;
  newspaperWebsite?: string;
  advertisementNo?: string;
  advertisementDate?: string;
  organisationInWhich?: string;
  lastDateOfApplica?: string;
  dandARPenaltyEffective?: string;
  vigilancePenaltyEffective?: string;
  dateOfConfirmation?: string;
  threeyearsRegularServ?: string;
  eligible?: string;
  placeAbroadTrain?: string;
  yearOfAbroadTrain?: string;
  dandARPendingOrNot?: string;
  vigilancePending?: string;
  dandARStatus?: string;
  finalDandARVigilance?: string;
  longIDNumber?: string;
  parentCompany?: string;
  concernedDirector?: string;
  requestCreationDat?: string;
  requestStatus?: string;
  approverLevel?: string;
  postAppliedfor?: string;
  organisation?: string;
  natureOfOutsideJob?: string;
  advertisementNo1?: string;
  advertisementDate1?: string;
  sap_created_at?: string;
  sap_modified_at?: string;
  natureDetails?: {
    created_at: string;
    modified_at: string;
    deleted_at: string;
    id: number;
    natureId: string;
    natureTitle: string;
  };
  documents?: any[];
  education?: any[];
}

export interface SapWorklistState {
  worklistData: {
    success: boolean;
    data: WorklistItem[];
    totalPages: number;
    currentPage: number;
    totalItems: number;
  };
  nocData: NOCData | null;
  loading: boolean;
  nocLoading: boolean;
  actionLoading: {
    taskId: number | null;
    action: "A" | "R" | null;
  };
  error: string | null;
  nocError: string | null;
  selectedTask: WorklistItem | null;
  pagination: {
    page: number;
    limit: number;
    total: number;
    totalPages: number;
  };
  nocDropdownData: any | null;
  syncLoading: boolean;
}

// API response interfaces
interface WorklistResponse {
  success: boolean;
  data: WorklistItem[];
  totalPages: number;
  currentPage: number;
  totalItems: number;
}

interface NOCDataResponse {
  status: number;
  message: string;
  data: NOCData;
}

// Request parameter interfaces
interface FetchWorklistParams {
  page?: string;
  limit?: string;
  orderColumn?: string;
  orderBy?: string;
  query?: string;
  filters?: {
    dateRange?: {
      type?: string;
      from?: string;
      to?: string;
    };
    moduleType?: string;
    claimType?: string;
    referenceNumber?: string;
  };
}

interface NOCDataParams {
  requestID: string;
  type:
  | "NOC_OUTJOB"
  | "NOC_EXINDIA"
  | "NOC_HIGHERSTUDY"
  | "NOC_PASSPORT"
  | "NOC_ONLINECOURSE-REIMB"
  | "NOC_ONLINECOURSE";
}

interface ApproveRejectParams {
  Approve_Reject_flag: "A" | "R";
  ReferenceNumber?: string;
  Remarks: string;
}

// Initial state
const initialState: SapWorklistState = {
  worklistData: {
    success: false,
    data: [],
    totalPages: 0,
    currentPage: 1,
    totalItems: 0,
  },
  nocData: null,
  loading: false,
  nocLoading: false,
  actionLoading: {
    taskId: null,
    action: null,
  },
  error: null,
  nocError: null,
  selectedTask: null,
  pagination: {
    page: 1,
    limit: 10,
    total: 0,
    totalPages: 0,
  },
  nocDropdownData: null,
  syncLoading: false,
};

// Async thunk for fetching SAP Master Worklist
export const fetchSapWorklist = createAsyncThunk(
  "sapWorklist/fetchWorklist",
  async (params: FetchWorklistParams = {}, { rejectWithValue }) => {
    try {
      console.log("Fetching SAP worklist with params:", params);

      const requestData = {
        page: params.page || "1",
        limit: params.limit || "10",
        orderColumn: params.orderColumn || "created_at",
        orderBy: params.orderBy || "DESC",
        query: params.query || "",
        filters: params.filters || {},
      };

      const result = await Api.post(
        API_URLS.SAP_MASTER_WORKLIST.FIND_ALL,
        requestData
      );

      console.log("SAP Worklist API response:", result);

      // Map API response to our interface based on actual response structure
      if (result.data && result.data.status === 200) {
        let apiData = result.data.data;
        let dataArray = [];
        
        if (Array.isArray(apiData)) {
          dataArray = apiData;
        } else if (apiData && Array.isArray(apiData.data)) {
          dataArray = apiData.data;
        } else if (Array.isArray(result.data)) {
          dataArray = result.data;
        }

        const transformedData = {
          success: true,
          data: dataArray,
          totalPages: apiData?.totalPages || result.data?.totalPages || 1,
          currentPage: parseInt(apiData?.page || result.data?.page || params.page || "1"),
          totalItems: apiData?.total || result.data?.total || dataArray.length,
        };
        console.log("Transformed SAP worklist data:", transformedData);
        return transformedData as WorklistResponse;
      } else {
        console.log("SAP API response error:", result);
        throw new Error(
          result.data?.message || "Failed to fetch worklist data"
        );
      }
    } catch (error: any) {
      console.error("SAP Worklist fetch error:", error);
      return rejectWithValue(Api.getErrorMessage(error));
    }
  }
);

export const syncWorklistCurrentDay = createAsyncThunk(
  "sapWorklist/syncWorklist",
  async (_, { rejectWithValue }) => {
    try {
      const response = await Api.post(
        API_URLS.SAP_MASTER_WORKLIST.SYNC_CURRENT_DAY,
        {}
      );
      if (response.data && response.data.status === 200) {
        return response.data;
      } else {
        throw new Error(response.data?.message || "Failed to sync worklist data");
      }
    } catch (error: any) {
      console.error("SAP Worklist sync error:", error);
      return rejectWithValue(Api.getErrorMessage(error));
    }
  }
);

// Async thunk for fetching NOC data
export const fetchNocData = createAsyncThunk(
  "sapWorklist/fetchNocData",
  async (params: NOCDataParams, { rejectWithValue }) => {
    try {
      console.log("Fetching NOC data with params:", params);

      let response: any = null;
      try {
        response = await Api.post(
          API_URLS.SAP_MASTER_WORKLIST.GET_NOC_DATA,
          params
        );
      } catch (err) {
        console.warn("GET_NOC_DATA API failed, will try fallback if applicable:", err);
      }

      console.log("NOC Data API response:", response);

      let nocObj: any = null;
      if (response?.data?.data && typeof response.data.data === "object" && (response.data.data.id || response.data.data.referenceNumber || response.data.data.empID || response.data.data.empId)) {
        nocObj = response.data.data;
      } else if (response?.data && typeof response.data === "object" && (response.data.id || response.data.referenceNumber || response.data.empID || response.data.empId)) {
        nocObj = response.data;
      } else if (response && typeof response === "object" && (response.id || response.referenceNumber || response.empID || response.empId)) {
        nocObj = response;
      } else if (response?.data?.data && typeof response.data.data === "object") {
        nocObj = response.data.data;
      } else if (response?.data && typeof response.data === "object") {
        nocObj = response.data;
      } else if (response && typeof response === "object") {
        nocObj = response;
      }

      // Fallback for NOC_ONLINECOURSE-REIMB if not found in GET_NOC_DATA
      if ((!nocObj || (!nocObj.id && !nocObj.referenceNumber && !nocObj.empID && !nocObj.empId)) && params.type === "NOC_ONLINECOURSE-REIMB") {
        try {
          const fallbackRes = await Api.post(API_URLS.NOC_REIMBURSEMENT.FIND_ALL, {
            query: params.requestID,
          });
          const list = fallbackRes?.data?.data?.data || fallbackRes?.data?.data || fallbackRes?.data;
          if (Array.isArray(list) && list.length > 0) {
            const found = list.find((item: any) =>
              String(item.referenceNumber) === String(params.requestID) ||
              String(item.id) === String(params.requestID) ||
              String(item.referenceNumber1) === String(params.requestID)
            );
            if (found) nocObj = found;
            else nocObj = list[0];
          }
        } catch (fbErr) {
          console.error("NOC_REIMBURSEMENT fallback error:", fbErr);
        }

        if (!nocObj && !isNaN(Number(params.requestID))) {
          try {
            const getOneRes = await Api.post(API_URLS.NOC_REIMBURSEMENT.FIND_ONE, {
              id: parseInt(params.requestID, 10),
            });
            if (getOneRes?.data?.data) {
              nocObj = getOneRes.data.data;
            } else if (getOneRes?.data) {
              nocObj = getOneRes.data;
            }
          } catch (getOneErr) {
            console.error("NOC_REIMBURSEMENT find-one fallback error:", getOneErr);
          }
        }
      }

      if (nocObj && (nocObj.id || nocObj.referenceNumber || nocObj.empID || nocObj.empId || Object.keys(nocObj).length > 0)) {
        return nocObj as NOCData;
      } else {
        throw new Error(
          response?.data?.message || response?.message || "Failed to fetch NOC data"
        );
      }
    } catch (error: any) {
      console.error("NOC data fetch error:", error);
      return rejectWithValue(Api.getErrorMessage(error));
    }
  }
);

// Async thunk for fetching NOC DnAR Dropdown data
export const fetchNocDnARDropdownData = createAsyncThunk(
  "sapWorklist/fetchNocDnARDropdownData",
  async (_, { rejectWithValue }) => {
    try {
      const response = await Api.post(
        API_URLS.SAP_MASTER_WORKLIST.GET_NOC_DNAR_DROPDOWN_DATA,
        {}
      );
      if (response.data && response.data.data) {
        return response.data.data;
      } else {
        throw new Error(
          response.data?.data?.message ||
          "Failed to fetch NOC DnAR Dropdown data"
        );
      }
    } catch (error: any) {
      console.error("NOC DnAR Dropdown data fetch error:", error);
      return rejectWithValue(Api.getErrorMessage(error));
    }
  }
);

// Async thunk for approve/reject action
export const approveRejectTask = createAsyncThunk(
  "sapWorklist/approveReject",
  async (
    params: { action: "A" | "R"; task: WorklistItem },
    { rejectWithValue }
  ) => {
    try {
      console.log("Approve/Reject action with params:", params);

      const requestBody: ApproveRejectParams = {
        Approve_Reject_flag: params.action,
        ReferenceNumber: params.task.requestID,
        Remarks: "Check remarks",
      };

      const result = await Api.post(
        API_URLS.SAP_MASTER_WORKLIST.APPROVE_REJECT_ACTION,
        requestBody
      );

      console.log("Approve/Reject API response:", result);

      if (result.data && result.data.status === 200) {
        return {
          success: true,
          action: params.action,
          taskId: params.task.id,
          message:
            result.data.message ||
            `Task ${params.action === "A" ? "approved" : "rejected"
            } successfully`,
        };
      } else {
        throw new Error(
          result.data?.message ||
          `Failed to ${params.action === "A" ? "approve" : "reject"} task`
        );
      }
    } catch (error: any) {
      console.error("Approve/Reject error:", error);
      return rejectWithValue(Api.getErrorMessage(error));
    }
  }
);

// Create the slice
const sapWorklistSlice = createSlice({
  name: "sapWorklist",
  initialState,
  reducers: {
    clearError: (state) => {
      state.error = null;
    },
    clearNocError: (state) => {
      state.nocError = null;
    },
    clearNocData: (state) => {
      state.nocData = null;
      state.nocError = null;
    },
    setSelectedTask: (state, action: PayloadAction<WorklistItem | null>) => {
      state.selectedTask = action.payload;
    },
    setCurrentPage: (state, action: PayloadAction<number>) => {
      state.pagination.page = action.payload;
      state.worklistData.currentPage = action.payload;
    },
    setPageLimit: (state, action: PayloadAction<number>) => {
      state.pagination.limit = action.payload;
    },
    setActionLoading: (
      state,
      action: PayloadAction<{ taskId: number | null; action: "A" | "R" | null }>
    ) => {
      state.actionLoading = action.payload;
    },
    clearActionLoading: (state) => {
      state.actionLoading = { taskId: null, action: null };
    },
  },
  extraReducers: (builder) => {
    builder
      // Fetch SAP Worklist
      .addCase(fetchSapWorklist.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchSapWorklist.fulfilled, (state, action) => {
        state.loading = false;
        state.worklistData = action.payload;
        state.pagination = {
          page: action.payload.currentPage,
          limit: state.pagination.limit,
          total: action.payload.totalItems,
          totalPages: action.payload.totalPages,
        };
      })
      .addCase(fetchSapWorklist.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
      })

      // Fetch NOC Data
      .addCase(fetchNocData.pending, (state) => {
        state.nocLoading = true;
        state.nocError = null;
      })
      .addCase(fetchNocData.fulfilled, (state, action) => {
        state.nocLoading = false;
        state.nocData = action.payload;
      })
      .addCase(fetchNocData.rejected, (state, action) => {
        state.nocLoading = false;
        state.nocError = action.payload as string;
      })

      // Fetch NOC Dropdown Data
      .addCase(fetchNocDnARDropdownData.pending, () => {
        // Keep separate from main nocLoading
      })
      .addCase(fetchNocDnARDropdownData.fulfilled, (state, action) => {
        state.nocDropdownData = action.payload;
      })
      .addCase(fetchNocDnARDropdownData.rejected, () => {
        // Keep separate from main nocError
      })

      // Approve/Reject Task
      .addCase(approveRejectTask.pending, (state, action) => {
        state.actionLoading = {
          taskId: action.meta.arg.task.id,
          action: action.meta.arg.action,
        };
      })
      .addCase(approveRejectTask.fulfilled, (state, action) => {
        state.actionLoading = { taskId: null, action: null };
        // You might want to update the task status in the worklist data here
        const updatedData = state.worklistData.data.map((task) => {
          if (task.id === action.payload.taskId) {
            return {
              ...task,
              status: action.payload.action === "A" ? "APPROVED" : "REJECTED",
            };
          }
          return task;
        });
        state.worklistData.data = updatedData;
      })
      .addCase(approveRejectTask.rejected, (state, action) => {
        state.actionLoading = { taskId: null, action: null };
        state.error = action.payload as string;
      })
      
      // Sync Worklist
      .addCase(syncWorklistCurrentDay.pending, (state) => {
        state.syncLoading = true;
        state.error = null;
      })
      .addCase(syncWorklistCurrentDay.fulfilled, (state) => {
        state.syncLoading = false;
      })
      .addCase(syncWorklistCurrentDay.rejected, (state, action) => {
        state.syncLoading = false;
        state.error = action.payload as string;
      });
  },
});

export const {
  clearError,
  clearNocError,
  clearNocData,
  setSelectedTask,
  setCurrentPage,
  setPageLimit,
  setActionLoading,
  clearActionLoading,
} = sapWorklistSlice.actions;

export default sapWorklistSlice.reducer;
